import CloseIcon from '@mui/icons-material/Close';
import { ArrowRightIcon } from '@mui/x-date-pickers';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { MultiPanelContainer } from '@src/common/components/MultiPanelContainer';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import type { FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { AxleTireSuspensionSection } from '@src/permitTypesManager/permitTypeSections/AxleTireSuspensionSection';
import { ConditionsSheetsSection } from '@src/permitTypesManager/permitTypeSections/ConditionsSheetsSection';
import { CustomDocumentPdfSection } from '@src/permitTypesManager/permitTypeSections/CustomDocumentPdfSection';
import { DriverInformationSection } from '@src/permitTypesManager/permitTypeSections/DriverInformationSection';
import { JurisdictionIdNumberSection } from '@src/permitTypesManager/permitTypeSections/JurisdictionIdNumberSection';
import { MobileHomeSection } from '@src/permitTypesManager/permitTypeSections/MobileHomeSection';
import { PermitDefinitionSection } from '@src/permitTypesManager/permitTypeSections/PermitDefinitionSection';
import { PermitInstructionsSection } from '@src/permitTypesManager/permitTypeSections/PermitInstructionsSection';
import { PermitInsuranceSection } from '@src/permitTypesManager/permitTypeSections/PermitInsuranceSection';
import { PermitLoadSection } from '@src/permitTypesManager/permitTypeSections/PermitLoadSection';
import { PermitTractorSection } from '@src/permitTypesManager/permitTypeSections/PermitTractorSection';
import { TrailerSection } from '@src/permitTypesManager/permitTypeSections/TrailerSection';
import { TripInformationSection } from '@src/permitTypesManager/permitTypeSections/TripInformationSection';
import { Navigation } from '@src/routing/Navigation';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  form: UseFormReturn<AddPermitTypeFormInputs>;
  pageTitle: string;
  submitButtonText: string;
  onSubmitAction: SubmitHandler<AddPermitTypeFormInputs>;
  conditionSheets: FormConditionSheet[];
}

export const PermitTypeManagementSheet = ({
  form,
  pageTitle,
  submitButtonText,
  onSubmitAction,
  conditionSheets,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const returnToPermitManager = () => {
    navigate(
      `/${Navigation.settings.PATH}?${Navigation.settings.searchParamKey}=0`,
    );
  };

  const firstPanel = {
    sections: [
      {
        title: t('permitTypesManager.addPermitType.definition.title'),
        content: <PermitDefinitionSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.instructions.title'),
        subtitle: t('permitTypesManager.addPermitType.instructions.subtitle'),
        content: <PermitInstructionsSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.customDocumentPDF.title'),
        subtitle: t(
          'permitTypesManager.addPermitType.customDocumentPDF.subtitle',
        ),
        content: <CustomDocumentPdfSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.jurisdictionIdNumber.title'),
        subtitle: t(
          'permitTypesManager.addPermitType.jurisdictionIdNumber.subtitle',
        ),
        content: <JurisdictionIdNumberSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.insurance.title'),
        subtitle: t('permitTypesManager.addPermitType.insurance.subtitle'),
        content: <PermitInsuranceSection />,
      },
    ],
  };

  const secondPanel = {
    sections: [
      {
        title: t('permitTypesManager.addPermitType.load.title'),
        subtitle: t('permitTypesManager.addPermitType.load.subtitle'),
        content: <PermitLoadSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.mobileHome.title'),
        subtitle: t('permitTypesManager.addPermitType.mobileHome.subtitle'),
        content: <MobileHomeSection />,
      },
    ],
  };

  const thirdPanel = {
    sections: [
      {
        title: t('permitTypesManager.addPermitType.tractor.title'),
        subtitle: t('permitTypesManager.addPermitType.tractor.subtitle'),
        content: <PermitTractorSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.driver.title'),
        subtitle: t('permitTypesManager.addPermitType.driver.subtitle'),
        content: <DriverInformationSection />,
      },
    ],
  };
  const fourthPanel = {
    sections: [
      {
        title: t('permitTypesManager.addPermitType.trailer.title'),
        subtitle: t('permitTypesManager.addPermitType.trailer.subtitle'),
        content: <TrailerSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.axleTireSuspension.title'),
        subtitle: t(
          'permitTypesManager.addPermitType.axleTireSuspension.subtitle',
        ),
        content: <AxleTireSuspensionSection />,
      },
      {
        title: t('permitTypesManager.addPermitType.trip.title'),
        subtitle: t('permitTypesManager.addPermitType.trip.subtitle'),
        content: <TripInformationSection />,
      },
    ],
  };

  const panels = [firstPanel, secondPanel, thirdPanel, fourthPanel];

  return (
    <PageLayout title={pageTitle}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmitAction)}>
          <Stack className={container}>
            <MultiPanelContainer panels={panels} />
            <ConditionsSheetsSection conditionsSheets={conditionSheets} />
          </Stack>
          <Divider
            style={{ marginBottom: theme.spacing['6'], marginTop: '40px' }}
          />

          <Stack direction="row" justifyContent="space-between">
            <Button
              type="button"
              className={button}
              onClick={returnToPermitManager}
            >
              <CloseIcon style={{ marginRight: theme.spacing['2'] }} />
              {t('cancel')}
            </Button>

            <Button
              className={button}
              variant="contained"
              disabled={false}
              type="submit"
            >
              {submitButtonText}
              <ArrowRightIcon style={{ marginLeft: theme.spacing['2'] }} />
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </PageLayout>
  );
};

const button = css({
  padding: '0 18px',
  height: '40px',
});

const container = css({
  backgroundColor: theme.palette.surface.surfaceContainerLowest,
  borderRadius: theme.radius.around,
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: colors.border.primary,
  boxShadow: '1px 1px 4px rgba(0, 0, 0, 0.1)',
  padding: theme.spacing['5'],
  flexDirection: 'column',
  gap: theme.spacing['3'],
  height: 'fit-content',
});
